import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../utils/api'
import AdminLayout from '../components/AdminLayout';
import { useSortableData, SortDirection } from '../components/SortableTable';

interface Transaction {
  id: number;
  reason: string;
  createdAt: string;
  who: string;
  type: string;
  amount: number;
  category: string;
  storyId?: number;
  invoiceCreditMemo?: string;
  postedDate: string;
}

interface TransactionGroup {
  parentCategory: string;
  creditTotal: number;
  debitTotal: number;
  balance: number;
  transactionCount: number;
  transactions: Transaction[];
}

interface BrandProfile {
  id: number;
  name: string;
  accountName: string;
  currentContentStrategyId?: number;
}

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 16px;
  color: #6b7280;
  a { color: #00a6c2; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #d97706;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: right;
  a { color: #00a6c2; text-decoration: none; font-size: 14px; &:hover { text-decoration: underline; } }
`;

const TabNav = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 24px;
`;

const TabLink = styled(Link)<{ $active?: boolean }>`
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ $active }) => ($active ? '#00a6c2' : '#6b7280')};
  text-decoration: none;
  border-bottom: 2px solid ${({ $active }) => ($active ? '#00a6c2' : 'transparent')};
  margin-bottom: -1px;
  &:hover { color: #00a6c2; background: #f9fafb; }
`;

const Section = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 24px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 20px 0;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 12px;
`;

const CategoryTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 24px 0 12px 0;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 8px;
`;

const SubTitle = styled.h4`
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin: 16px 0 8px 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 16px;
`;

const Th = styled.th<{ $sortable?: boolean; $sorted?: boolean }>`
  text-align: left;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $sorted }) => ($sorted ? '#00a6c2' : '#374151')};
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  user-select: none;
  white-space: nowrap;
  
  &:hover {
    ${({ $sortable }) => $sortable && `
      background: #f0f0f0;
      color: #00a6c2;
    `}
  }
`;

const SortIndicator = styled.span<{ $direction: SortDirection }>`
  margin-left: 4px;
  
  &::after {
    content: '${({ $direction }) => {
      if ($direction === 'asc') return '↓';
      if ($direction === 'desc') return '↑';
      return '';
    }}';
  }
`;

const Td = styled.td`
  padding: 10px 12px;
  font-size: 14px;
  color: #374151;
  border: 1px solid #e5e7eb;
`;

const ResultRow = styled.tr`
  background: #f9fafb;
  font-weight: 500;
`;

const TransactionLink = styled(Link)`
  color: #00a6c2;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const StoryLink = styled.a`
  color: #00a6c2;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const EmptyRow = styled.div`
  padding: 16px;
  color: #6b7280;
  background: #f9fafb;
  border-radius: 4px;
  text-align: center;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
`;

const formatDollars = (cents: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(cents / 100);
};

// Helper component for sortable transaction table within each group
interface TransactionsTableProps {
  transactions: Transaction[];
  formatDollars: (cents: number) => string;
}

function TransactionsTable({ transactions, formatDollars }: TransactionsTableProps) {
  const { sortedData, requestSort, getSortDirection } = useSortableData(transactions);

  return (
    <Table>
      <thead>
        <tr>
          <Th $sortable $sorted={getSortDirection('id') !== null} onClick={() => requestSort('id')}>
            Id <SortIndicator $direction={getSortDirection('id')} />
          </Th>
          <Th $sortable $sorted={getSortDirection('reason') !== null} onClick={() => requestSort('reason')}>
            Reason <SortIndicator $direction={getSortDirection('reason')} />
          </Th>
          <Th $sortable $sorted={getSortDirection('createdAt') !== null} onClick={() => requestSort('createdAt')}>
            Created <SortIndicator $direction={getSortDirection('createdAt')} />
          </Th>
          <Th $sortable $sorted={getSortDirection('who') !== null} onClick={() => requestSort('who')}>
            Who <SortIndicator $direction={getSortDirection('who')} />
          </Th>
          <Th $sortable $sorted={getSortDirection('type') !== null} onClick={() => requestSort('type')}>
            Type <SortIndicator $direction={getSortDirection('type')} />
          </Th>
          <Th $sortable $sorted={getSortDirection('amount') !== null} onClick={() => requestSort('amount')}>
            Amount <SortIndicator $direction={getSortDirection('amount')} />
          </Th>
          <Th $sortable $sorted={getSortDirection('category') !== null} onClick={() => requestSort('category')}>
            Category <SortIndicator $direction={getSortDirection('category')} />
          </Th>
          <Th $sortable $sorted={getSortDirection('storyId') !== null} onClick={() => requestSort('storyId')}>
            Story Id <SortIndicator $direction={getSortDirection('storyId')} />
          </Th>
          <Th $sortable $sorted={getSortDirection('invoiceCreditMemo') !== null} onClick={() => requestSort('invoiceCreditMemo')}>
            Invoice/Credit memo <SortIndicator $direction={getSortDirection('invoiceCreditMemo')} />
          </Th>
          <Th $sortable $sorted={getSortDirection('postedDate') !== null} onClick={() => requestSort('postedDate')}>
            Posted Date <SortIndicator $direction={getSortDirection('postedDate')} />
          </Th>
        </tr>
      </thead>
      <tbody>
        {sortedData.map((t) => (
          <tr key={t.id}>
            <Td>
              <TransactionLink to={`/admin2/transactions/${t.id}`}>{t.id}</TransactionLink>
            </Td>
            <Td>{t.reason}</Td>
            <Td>{t.createdAt}</Td>
            <Td>{t.who}</Td>
            <Td>{t.type}</Td>
            <Td>{formatDollars(t.amount)}</Td>
            <Td>{t.category}</Td>
            <Td>
              {t.storyId && (
                <StoryLink href={`/stories/${t.storyId}`} target="_blank">{t.storyId}</StoryLink>
              )}
            </Td>
            <Td>{t.invoiceCreditMemo || ''}</Td>
            <Td>{t.postedDate}</Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function AdminPublicationTransactionsPage() {
  const { id } = useParams<{ id: string }>();
  const [brandProfile, setBrandProfile] = useState<BrandProfile | null>(null);
  const [overall, setOverall] = useState({ creditTotal: 0, debitTotal: 0, balance: 0 });
  const [groups, setGroups] = useState<TransactionGroup[]>([]);
  const [loading, setLoading] = useState(true);

  // Sorting for category summary table
  const { sortedData: sortedGroups, requestSort: requestGroupSort, getSortDirection: getGroupSortDirection } = useSortableData(groups);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get(`/admin2/publications/${id}/transactions.json`);
      setBrandProfile(res.data.brandProfile);
      setOverall(res.data.overall);
      setGroups(res.data.groups || []);
    } catch (err) {
      console.error('Failed to load data', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const pubDisplayName = brandProfile ? `${brandProfile.accountName} - ${brandProfile.name}` : 'Publication';

  if (loading) {
    return (
      <AdminLayout 
        title="Publications" 
        breadcrumb={[
          { label: 'Publications', href: '/admin2/publications' },
          { label: 'Loading...' }
        ]}
      >
        <LoadingState>Loading...</LoadingState>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout 
      title="Publications" 
      breadcrumb={[
        { label: 'Publications', href: '/admin2/publications' },
        { label: pubDisplayName, href: `/admin2/publications/${id}/edit` },
        { label: 'Transactions' }
      ]}
    >
      <Breadcrumb>
        <Link to="/admin2/publications">Publications</Link>
        <span>›</span>
        <Link to={`/admin2/publications/${id}/edit`}>{brandProfile?.accountName} - {brandProfile?.name}</Link>
        <span>›</span>
        <span>Transactions</span>
      </Breadcrumb>

      <PageHeader>
        <PageTitle>Edit {brandProfile?.accountName} - {brandProfile?.name}</PageTitle>
        <HeaderActions>
          <a href={`/admin2/publications/${id}/talent_requests`}>Manage talent requests</a>
          {brandProfile?.currentContentStrategyId && (
            <Link to={`/admin2/content_strategies/${brandProfile.currentContentStrategyId}/edit`}>
              Manage content strategy
            </Link>
          )}
        </HeaderActions>
      </PageHeader>

      <TabNav>
        <TabLink to={`/admin2/publications/${id}/edit`}>Publication info</TabLink>
        <TabLink to={`/admin2/publications/${id}/users`}>Users</TabLink>
        <TabLink to={`/admin2/publications/${id}/transactions`} $active>Transactions</TabLink>
        <TabLink to={`/admin2/publications/${id}/fixed_arr_credits`}>Fixed ARR Credits</TabLink>
        <TabLink to={`/admin2/publications/${id}/bulk_story_tagging`}>Bulk Story Tagging</TabLink>
        <TabLink to={`/admin2/publications/${id}/talent_requests`}>Talent Requests</TabLink>
      </TabNav>

      <Section>
        <SectionTitle>Transactions</SectionTitle>

        {/* Overall Summary */}
        <Table>
          <thead>
            <tr>
              <Th>Total Credit</Th>
              <Th>Total Debit</Th>
              <Th>Balance</Th>
            </tr>
          </thead>
          <tbody>
            <ResultRow>
              <Td>{formatDollars(overall.creditTotal)}</Td>
              <Td>{formatDollars(overall.debitTotal)}</Td>
              <Td>{formatDollars(overall.balance)}</Td>
            </ResultRow>
          </tbody>
        </Table>

        {/* Category Summary */}
        <Table>
          <thead>
            <tr>
              <Th $sortable $sorted={getGroupSortDirection('parentCategory') !== null} onClick={() => requestGroupSort('parentCategory')}>
                Parent Category <SortIndicator $direction={getGroupSortDirection('parentCategory')} />
              </Th>
              <Th $sortable $sorted={getGroupSortDirection('creditTotal') !== null} onClick={() => requestGroupSort('creditTotal')}>
                Total Credit <SortIndicator $direction={getGroupSortDirection('creditTotal')} />
              </Th>
              <Th $sortable $sorted={getGroupSortDirection('debitTotal') !== null} onClick={() => requestGroupSort('debitTotal')}>
                Total Debit <SortIndicator $direction={getGroupSortDirection('debitTotal')} />
              </Th>
              <Th $sortable $sorted={getGroupSortDirection('balance') !== null} onClick={() => requestGroupSort('balance')}>
                Balance <SortIndicator $direction={getGroupSortDirection('balance')} />
              </Th>
            </tr>
          </thead>
          <tbody>
            {sortedGroups.map((group) => (
              <ResultRow key={group.parentCategory}>
                <Td>{group.parentCategory}</Td>
                <Td>{formatDollars(group.creditTotal)}</Td>
                <Td>{formatDollars(group.debitTotal)}</Td>
                <Td>{formatDollars(group.balance)}</Td>
              </ResultRow>
            ))}
          </tbody>
        </Table>

        {/* Detailed Transactions by Category */}
        {sortedGroups.map((group) => (
          <div key={group.parentCategory}>
            <CategoryTitle>{group.parentCategory}</CategoryTitle>
            
            <Table>
              <thead>
                <tr>
                  <Th>Credit</Th>
                  <Th>Debit</Th>
                  <Th>Balance</Th>
                </tr>
              </thead>
              <tbody>
                <ResultRow>
                  <Td>{formatDollars(group.creditTotal)}</Td>
                  <Td>{formatDollars(group.debitTotal)}</Td>
                  <Td>{formatDollars(group.balance)}</Td>
                </ResultRow>
              </tbody>
            </Table>

            <SubTitle>{group.transactionCount} Transactions</SubTitle>
            
            {group.transactions.length === 0 ? (
              <EmptyRow>0 results</EmptyRow>
            ) : (
              <TransactionsTable transactions={group.transactions} formatDollars={formatDollars} />
            )}
          </div>
        ))}
      </Section>
    </AdminLayout>
  );
}

export default AdminPublicationTransactionsPage;
